import React from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { InputNumber } from "primereact/inputnumber";
import { InputTextarea } from "primereact/inputtextarea";

interface ITeamMember {
  id: number;
  name: string;
  currentLeads: number;
  emails: number;
  calls: number;
  status?: string;
}

interface LeadAllocationFormProps {
  visible: boolean;
  onHide: () => void;
  unassignedCount: number;
  teamMembers: ITeamMember[];
  allocationValues: { [key: number]: number };
  selectedMemberId: number | null;
  onAllocateChange: (memberId: number, val: number | null) => void;
  description: string;
  onDescriptionChange: (val: string) => void;
  onSave: () => void;
  loading: boolean;
}

const getStatusColor = (status?: string) => {
  switch (status) {
    case "Logged In":
      return "#22c55e";
    case "Logged Out":
      return "#ef4444"; 
    case "Not Logged In Yet":
    default:
      return "#708090"; // slate gray / neutral
  }
};

export const LeadAllocationForm: React.FC<LeadAllocationFormProps> = ({
  visible,
  onHide,
  unassignedCount,
  teamMembers,
  allocationValues,
  selectedMemberId,
  onAllocateChange,
  description,
  onDescriptionChange,
  onSave,
  loading,
}) => {
  const dialogFooter = (
    <div className="flex justify-content-end gap-2">
      <Button
        label="Cancel"
       // icon="pi pi-times"
        className="p-button-text p-button-secondary"
        onClick={onHide}
      />
      <Button
        label="Save"
      //  icon="pi pi-check"
        className="p-button-success"
        onClick={onSave}
        loading={loading}
      />
    </div>
  );

  return (
    <Dialog
      header="Allocate Fresh Leads"
      visible={visible}
      style={{ width: "500px" }}
      footer={dialogFooter}
      onHide={onHide}
      className="p-fluid"
      modal
    >
      <div className="flex flex-column gap-3 mt-2">
        <div className="flex justify-content-between align-items-center p-3 bg-primary-50 border-round mb-2 text-primary-900">
          <span className="font-medium">
            Total Available Fresh Leads:
          </span>
          <span className="font-bold text-xl">{unassignedCount}</span>
        </div>

        <div className="max-h-20rem overflow-y-auto pr-1">
          {teamMembers.map((member) => (
            <div
              key={member.id}
              className={`flex justify-content-between align-items-center mb-3 p-2 border-round ${
                selectedMemberId === member.id
                  ? "bg-highlight-50 border-1 border-primary-200"
                  : ""
              }`}
            >
              <div className="flex-grow-1 pr-2">
                <div className="font-medium text-800">{member.name}</div>
                <div className="text-xs mt-1" style={{ color: getStatusColor(member.status) }}>
                  Status: <span className="font-semibold">{member.status || "Not Logged In Yet"}</span>
                </div>
                <div className="text-xs text-color-secondary mt-1">
                  Current Leads: {member.currentLeads}
                </div>
              </div>
              <div
                style={{
                  width: "120px",
                  opacity: member.status !== "Logged In" ? 0.6 : 1,
                  cursor: member.status !== "Logged In" ? "not-allowed" : "default",
                }}
              >
                <InputNumber
                  value={allocationValues[member.id] || 0}
                  onValueChange={(e) => onAllocateChange(member.id, e.value)}
                  min={0}
                  max={Math.min(unassignedCount, 500)}
                  showButtons
                  buttonLayout="horizontal"
                  incrementButtonIcon="pi pi-plus"
                  decrementButtonIcon="pi pi-minus"
                  inputStyle={{
                    textAlign: "center",
                    cursor: member.status !== "Logged In" ? "not-allowed" : "text",
                  }}
                  disabled={member.status !== "Logged In"}
                />
              </div>
            </div>
          ))}
        </div>

        <div className="flex flex-column gap-2 mt-2">
          <label htmlFor="allocation-description" className="font-semibold text-800 text-sm">
            Description / Allocation Notes
          </label>
          <InputTextarea
            id="allocation-description"
            value={description}
            onChange={(e) => onDescriptionChange(e.target.value)}
            rows={3}
            autoResize
            placeholder="Enter allocation details or notes here..."
            className="w-full"
          />
        </div>
      </div>
    </Dialog>
  );
};
